import React, { useState, useEffect, useCallback } from 'react';
import { createRecordInAirTable, fetchFlows, searchRecordInAirTable } from '../functions.js/airTableDB';
import WorkflowBuilder from '../components/workflowBuilder';
import Select from 'react-select';
import {
  getSpaces,
  getFolders,
  getFolderlessLists,
  getFolderLists,
  getListData,
  getListMembers,
  getListTasks
} from '../functions.js/clickup_api';
import './startWorkflow.css';
import { calculateDueDates, flattenFlow } from '../functions.js/calcFlow';

// Debug-Build-Markierung
console.log('[newWorkflow] loaded build 2025-10-07T01');


/*==========================================================
  Hilfsfunktionen: Array-Vergleich & Objektvergleich
==========================================================*/
// Einfachster Weg: JSON-String-Vergleich.
// Für sehr große Arrays/Objekte kann man das optimieren,
// aber hier reicht's vollkommen.
function isSameArray(arrA, arrB) {
  return JSON.stringify(arrA) === JSON.stringify(arrB);
}
function isSameObject(objA, objB) {
  return JSON.stringify(objA) === JSON.stringify(objB);
}

/*==========================================================
  STEP 3: Rollen-Matching (MatchBox)
==========================================================*/
/**
 * MatchBox:
 *  - Rollen -> Member-Auswahl
 *  - Startdatum
 *  - Freigabe-Status
 *  - Naming-Convention (Checkboxes)
 *  - "Workflow starten"-Button
 */
function MatchBox({
  clickupListMembers = [],
  clickUpListStatis = [],
  clickUpListID = "",
  clickupListTasks = [],
  savedStaticFlowJson = null,
  onStartWorkflow
}) {
  // Nehmen wir an, savedStaticFlowJson.roles = ['Grafiker', 'Projektleiter', ...]
  const roles = Array.isArray(savedStaticFlowJson?.roles)
    ? savedStaticFlowJson.roles
    : [];

  // States
  const [roleAssignments, setRoleAssignments] = useState({});
  const [startDate, setStartDate] = useState('');

  const [unlockedStatus, setUnlockedStatus] = useState('');
  const [projektStatus, setProjektStatus] = useState('');

  const [workflowName, setWorkflowName] = useState('new Workflow');
  const [isUploading, setIsUploading] = useState(false);
  
  // Neuer Modus: Neu erzeugen vs. bestehenden Workflow erweitern
  const [workflowMode, setWorkflowMode] = useState('new'); // 'new' | 'extend'
  const [selectedExistingTaskId, setSelectedExistingTaskId] = useState('');
  
  // Sichtbarkeits-Log
  useEffect(() => {
    console.log('[MatchBox] render', {
      roles: roles.length,
      clickUpListID,
      tasks: Array.isArray(clickupListTasks) ? clickupListTasks.length : 0,
      workflowMode
    });
  }, [roles, clickUpListID, clickupListTasks, workflowMode]);
  
  // Debug: Props/State visibilisieren
  useEffect(() => {
    console.log('[MatchBox] mounted/updated', {
      roles: roles.length,
      clickUpListID,
      tasks: Array.isArray(clickupListTasks) ? clickupListTasks.length : 0,
      defaultMode: workflowMode
    });
  }, [roles, clickUpListID, clickupListTasks, workflowMode]);
  
  // Neuer State für Automatisierungen
  const [automations, setAutomations] = useState([]);
  const [selectedAutomation, setSelectedAutomation] = useState('');
  const [isLoadingAutomations, setIsLoadingAutomations] = useState(true);

  // Checkboxes für "Naming Convention"
  const [namingConvention, setNamingConvention] = useState({
    groupTaskInTask: true,
    workflowNameInTask: true
  });

  // Automatisierungen laden
  useEffect(() => {
    const fetchAutomations = async () => {
      try {
        const fetchedAutomations = await searchRecordInAirTable('automations');
        setAutomations(fetchedAutomations || []);
      } catch (error) {
        console.error('Fehler beim Laden der Automatisierungen:', error);
      } finally {
        setIsLoadingAutomations(false);
      }
    };
    
    fetchAutomations();
  }, []);

  // Rolle -> Member
  const handleRoleChange = (role, memberId) => {
    setRoleAssignments((prev) => ({
      ...prev,
      [role]: memberId,
    }));
  };

  // Checkbox-Handler
  const handleNamingConventionChange = (fieldName) => {
    setNamingConvention((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  // Validierung: Überprüfen, ob alle Felder ausgefüllt wurden
  const validate = () => {
    if (!workflowName.trim()) {
      alert('Bitte geben Sie einen Workflownamen ein.');
      return false;
    }
    if (!startDate) {
      alert('Bitte wählen Sie ein Startdatum aus.');
      return false;
    }
    if (!unlockedStatus) {
      alert('Bitte wählen Sie einen ToDo-Status aus.');
      return false;
    }
    if (!projektStatus) {
      alert('Bitte wählen Sie einen Projekt-Status aus.');
      return false;
    }
    // Für jede Rolle in savedStaticFlowJson prüfen, ob eine Zuordnung vorliegt
    for (const role of roles) {
      if (!roleAssignments[role]) {
        alert(`Bitte wählen Sie ein Mitglied für die Rolle "${role}" aus.`);
        return false;
      }
    }
    // Pflicht, wenn bestehenden Workflow erweitern gewählt wurde
    if (workflowMode === 'extend' && !selectedExistingTaskId) {
      alert('Bitte wählen Sie eine bestehende ClickUp-Aufgabe aus.');
      return false;
    }
    return true;
  };

  // Button-Klick
  const handleUploadWorkflow = () => {
    // Validierung vor Versand
    if (!validate()) {
      return;
    }
    // Button sofort deaktivieren, um doppelte Erstellung zu verhindern
    setIsUploading(true);

    const payload = {
      startDate,
      unlockedStatus,
      projektStatus,
      roleAssignments,
      flowJson: savedStaticFlowJson,
      namingConvention,
      clickUpListID,
      workflowName: workflowName,
      namingConvention: namingConvention,
      automatisierung: selectedAutomation,  // Neue Automatisierungs-ID hinzufügen
      workflowMode,
      bestands_workdlow_id: selectedExistingTaskId
    };
    onStartWorkflow?.(payload);
  };

  return (
    <div className="step matchbox-container">
      {/* Header */}
      <div className="step-header">
        <div className="step-number">3</div>
        <h4 className="step-title">Rollen &amp; Start</h4>
      </div>
      
      {/* Inhalt */}
      <div className="step-content">
        {/* Rollen-Zuweisung */}
        <div className="match-section">
          <p className="match-section-title">Rollen vergeben</p>
          <p className="match-section-desc">
            Ordne den einzelnen Rollen deine Team-Mitglieder zu. So wissen wir, wer später
            welche Aufgabe bekommt.
          </p>
          {roles.length === 0 && (
            <p className="no-roles-hint">Keine Rollen im Flow gefunden.</p>
          )}
          <div className="roles-wrapper">
            {roles.map((role) => (
              <div key={role} className="role-bubble">
                <label className="role-label">{role}:</label>
                <Select
                  className="select-box role-select"
                  value={roleAssignments[role] ? { 
                    value: roleAssignments[role], 
                    label: clickupListMembers.find(m => m.id === roleAssignments[role])?.username || clickupListMembers.find(m => m.id === roleAssignments[role])?.email || 'Unbekannt'
                  } : null}
                  onChange={(option) => handleRoleChange(role, option?.value)}
                  options={clickupListMembers.map((m) => ({
                    value: m.id,
                    label: m.username || m.email
                  }))}
                  placeholder="-- Mitglied wählen --"
                  isClearable
                  isSearchable
                  styles={customSelectStyles}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Workflowname */}
        <div className="match-section">
          <p className="match-section-title">Hauptaufgabe-Name (Beispiel: Lagermitarbeiter, Karriereportal)</p>
          <p className="match-section-desc">
            Wie sollte das Projekt heißen?
          </p>
          <input
            type="text"
            className="input-box"
            value={workflowName}
            onChange={(e) => setWorkflowName(e.target.value)}
          />
        </div>
        
        {/* Hauptaufgabe Status */}
        <div className="match-section">
          <p className="match-section-title">Hauptaufgabe-Status (Beispiel: Projekt, Workflow, Website, Kampagne,...)</p>
          <p className="match-section-desc">
            Wähle den Status, welcher dem Projekt zugeordnet werden sollte aus.
          </p>
          <Select
            className="select-box status-select"
            value={projektStatus ? { value: projektStatus, label: projektStatus } : null}
            onChange={(option) => setProjektStatus(option?.value)}
            options={clickUpListStatis?.map((st) => ({
              value: st.status,
              label: st.status
            }))}
            placeholder="-- Status wählen --"
            isClearable
            isSearchable
            styles={customSelectStyles}
          />
        </div>

        {/* Startdatum */}
        <div className="match-section">
          <p className="match-section-title">Umsetzungs-Startdatum</p>
          <p className="match-section-desc">
            Ab wann sollen die Aufgaben im Projekt kalendarisch gelten?
          </p>
          <input
            type="date"
            className="input-box date-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        
        {/* Freigabe-Status */}
        <div className="match-section">
          <p className="match-section-title">Freigeschaltete Unteraufgabe-Status (Beispiel: Zu Erledigen)</p>
          <p className="match-section-desc">
            Wähle hier den Status, ab dem die Unteraufgabe als "freigeschaltet" gilt. Heißt diese werden durch keine andere Aufgabe blockiert und der Mitarbeiter kann mit der bearbeitung starten.
          </p>
          <Select
            className="select-box status-select"
            value={unlockedStatus ? { value: unlockedStatus, label: unlockedStatus } : null}
            onChange={(option) => setUnlockedStatus(option?.value)}
            options={clickUpListStatis?.map((st) => ({
              value: st.status,
              label: st.status
            }))}
            placeholder="-- Status wählen --"
            isClearable
            isSearchable
            styles={customSelectStyles}
          />
        </div>

        {/* Workflow-Modus */}
        <div className="match-section" data-testid="workflow-mode-section">
          <p className="match-section-title">Workflow-Modus</p>
          <p className="match-section-desc">Entscheide, ob ein neuer Workflow erstellt oder ein bestehender erweitert werden soll.</p>
          <Select
            className="select-box status-select"
            value={{ value: workflowMode, label: workflowMode === 'new' ? 'Neuen Workflow erzeugen' : 'Bestehenden Workflow erweitern' }}
            onChange={(option) => {
              const val = option?.value || 'new';
              setWorkflowMode(val);
              if (val === 'new') {
                setSelectedExistingTaskId('');
              }
            }}
            options={[
              { value: 'new', label: 'Neuen Workflow erzeugen' },
              { value: 'extend', label: 'Bestehenden Workflow erweitern' }
            ]}
            isSearchable={false}
            styles={customSelectStyles}
          />
        </div>

        {/* Auswahl bestehender ClickUp-Aufgabe (nur wenn erweitern) */}
        {workflowMode === 'extend' && (
          <div className="match-section" data-testid="existing-task-select">
            <p className="match-section-title">Bestehende ClickUp-Aufgabe auswählen</p>
            <p className="match-section-desc">Es werden nur Aufgaben ohne Subtasks angezeigt.</p>
            <Select
              className="select-box status-select"
              value={selectedExistingTaskId ? { value: selectedExistingTaskId, label: clickupListTasks.find(t => t.id === selectedExistingTaskId)?.name || selectedExistingTaskId } : null}
              onChange={(option) => setSelectedExistingTaskId(option?.value || '')}
              options={clickupListTasks.map((t) => ({ value: t.id, label: t.name || t.id }))}
              placeholder="-- Aufgabe wählen --"
              isClearable
              isSearchable
              styles={customSelectStyles}
            />
          </div>
        )}

        {/* Neue Automatisierungen-Auswahl */}
        <div className="match-section">
          <p className="match-section-title">Automatisierung</p>
          <p className="match-section-desc">
            Wähle eine Automatisierung für diesen Workflow aus.
          </p>
          <Select
            className="select-box status-select"
            value={selectedAutomation ? { value: selectedAutomation, label: selectedAutomation } : null}
            onChange={(option) => setSelectedAutomation(option?.value)}
            options={automations.map((automation) => ({
              value: automation.fields?.name,
              label: automation.fields?.name || 'Unbenannte Automatisierung'
            }))}
            placeholder="-- Keine Automatisierung --"
            isClearable
            isSearchable
            isDisabled={isLoadingAutomations}
            styles={customSelectStyles}
          />
        </div>

        {/* Naming-Convention */}
        <div className="match-section">
          <p className="match-section-title">Naming-Convention</p>
          <p className="match-section-desc">
            Wähle aus, ob der Gruppenname oder der Workflowname automatisch
            im Titel der Aufgaben ergänzt werden soll.
          </p>
          <label className="bubble-checkbox">
            <span>Gruppennamen in Task-Titel übernehmen</span>
            <input
              type="checkbox"
              checked={namingConvention.groupTaskInTask}
              onChange={() => handleNamingConventionChange('groupTaskInTask')}
            />
          </label>
          <label className="bubble-checkbox">
            <span>Workflownamen in Task-Titel übernehmen</span>
            <input
              type="checkbox"
              checked={namingConvention.workflowNameInTask}
              onChange={() => handleNamingConventionChange('workflowNameInTask')}
            />
          </label>
        </div>

        {/* Button */}
        <div className="match-row">
          <button className="btn" onClick={handleUploadWorkflow} disabled={isUploading}>
            {isUploading ? "Bitte warten..." : "Workflow starten"}
          </button>
        </div>
      </div>
    </div>
  );
}

/*==========================================================
  STEP 2: ClickUp-Auswahl (ClickUpBlock)
==========================================================*/
function ClickUpBlock({
  userCookie,
  onSelectClickUpData
}) {

  /*==========================================================
    Beispiel-User-Cookie
  ==========================================================*/

  /*
  const userCookie = {
    workspace: ['recwtfLmXd6e5jrlv'],
    password: 'admin',
    email: 'kunden@fliessbandbetrieb.de',
    username: 'admin',
    f_name: 'admin',
    rec_id: 'recw67TlfHtdzn2IE',
    workspace_id: ['recwtfLmXd6e5jrlv'],
    workspace_name: ['Fliessbandbetrieb'],
    workspace_tool_apikey: ['pk_84036956_J1OZ74BP4M35HFSZNWJROEC75R2NX1A5'],
    workspace_tool_name: ['clickup'],
    clickup_workspace_id: ['24531314'],
  };
  */  

  const clickupApiKey = userCookie.workspace_tool_apikey?.[0];
  const clickupTeamId = userCookie.clickup_workspace_id?.[0];

  // 1) Spaces
  const [spaces, setSpaces] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState(null);

  // 2) Folder oder folderless
  const [folders, setFolders] = useState([]);
  const [folderlessLists, setFolderlessLists] = useState([]);
  const [folderMode, setFolderMode] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);

  // 3) Listen
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);

  // 4) Status & Mitglieder
  const [statuses, setStatuses] = useState([]);
  const [members, setMembers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  // Spaces laden
  useEffect(() => {
    if (!clickupApiKey || !clickupTeamId) return;
    (async () => {
      try {
        const fetchedSpaces = await getSpaces(clickupTeamId, clickupApiKey);
        setSpaces(fetchedSpaces);
        console.log('[ClickUpBlock] Spaces geladen:', fetchedSpaces?.length || 0);
      } catch (err) {
        console.error('Fehler beim Laden der Spaces:', err);
      }
    })();
  }, [clickupApiKey, clickupTeamId]);

  // Folder + folderlessLists laden
  useEffect(() => {
    if (!selectedSpace || !clickupApiKey) return;
    (async () => {
      try {
        const [fetchedFolders, fetchedFolderless] = await Promise.all([
          getFolders(selectedSpace, clickupApiKey),
          getFolderlessLists(selectedSpace, clickupApiKey),
        ]);
        setFolders(fetchedFolders);
        setFolderlessLists(fetchedFolderless);
        console.log('[ClickUpBlock] Folders:', fetchedFolders?.length || 0, 'Folderless:', fetchedFolderless?.length || 0);
      } catch (err) {
        console.error('Fehler beim Laden von Folder/Folderless:', err);
      }
    })();
  }, [selectedSpace, clickupApiKey]);

  // Listen laden
  useEffect(() => {
    if (!clickupApiKey) return;

    if (folderMode === 'folderless') {
      setLists(folderlessLists);
      return;
    }
    if (folderMode === 'folder' && selectedFolder) {
      (async () => {
        try {
          const fetchedLists = await getFolderLists(selectedFolder, clickupApiKey);
          setLists(fetchedLists);
          console.log('[ClickUpBlock] Listen im Ordner:', fetchedLists?.length || 0);
        } catch (err) {
          console.error('Fehler beim Laden der Listen:', err);
        }
      })();
    }
    if (folderMode === 'folder' && !selectedFolder) {
      setLists([]);
    }
  }, [folderMode, folderlessLists, selectedFolder, clickupApiKey]);

  // Status + Mitglieder
  useEffect(() => {
    if (!selectedList || !clickupApiKey) return;

    (async () => {
      try {
        console.log('[ClickUpBlock] Lade Details für Liste:', selectedList);
        const listDetails = await getListData(selectedList, clickupApiKey);
        if (listDetails?.statuses) {
          setStatuses(listDetails.statuses);
        }
        const listMems = await getListMembers(selectedList, clickupApiKey);
        setMembers(listMems);

        // Tasks ohne Subtasks laden
        setTasksLoading(true);
        try {
          const listTasks = await getListTasks(selectedList, clickupApiKey, { includeSubtasks: false });
          const filtered = Array.isArray(listTasks) ? listTasks.filter(t => !t.parent) : [];
          setTasks(filtered);
          console.log('[ClickUpBlock] Tasks (ohne Subtasks):', filtered?.length || 0);
        } finally {
          setTasksLoading(false);
        }
      } catch (error) {
        console.error('Fehler beim Laden der List-Daten:', error);
      }
    })();
  }, [selectedList, clickupApiKey]);

  // Daten "nach oben" geben, sobald sich listId / statuses / members ändern
  // -> Endlos-Schleife-Fix: Der Parent setzt nur den State, wenn sich etwas
  //    wirklich ändert (shallow/JSON-compare).
  useEffect(() => {
    if (!onSelectClickUpData) return;
    console.log('[ClickUpBlock] Sende nach oben:', {
      listId: selectedList,
      statuses: statuses?.length || 0,
      members: members?.length || 0,
      tasks: tasks?.length || 0
    });
    onSelectClickUpData({
      listId: selectedList,
      statuses,
      members,
      tasks
    });
  }, [selectedList, statuses, members, tasks, onSelectClickUpData]);

  return (
    <div className="step">
      <div className="step-header">
        <div className="step-number">2</div>
        <h4 className="step-title">ClickUp-Ziel wählen</h4>
      </div>

      <div className="step-content">
        {/* Space */}
        <Select
          className="select-box"
          value={selectedSpace ? { value: selectedSpace, label: spaces.find(sp => sp.id === selectedSpace)?.name } : null}
          onChange={(option) => {
            setSelectedSpace(option?.value || null);
            // Reset
            setFolderMode(null);
            setSelectedFolder(null);
            setLists([]);
            setSelectedList(null);
            setStatuses([]);
            setMembers([]);
          }}
          options={spaces.map((sp) => ({
            value: sp.id,
            label: sp.name
          }))}
          placeholder="Space wählen..."
          isClearable
          isSearchable
          styles={customSelectStyles}
        />

        {/* Folder oder folderless */}
        {selectedSpace && (
          <div style={{ display: 'flex', gap: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <input
                type="radio"
                name="folderMode"
                value="folder"
                checked={folderMode === 'folder'}
                onChange={() => {
                  setFolderMode('folder');
                  setSelectedFolder(null);
                  setLists([]);
                  setSelectedList(null);
                  setStatuses([]);
                  setMembers([]);
                }}
              />
              Kunde  ist in einem ClickUp-Ordner
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <input
                type="radio"
                name="folderMode"
                value="folderless"
                checked={folderMode === 'folderless'}
                onChange={() => {
                  setFolderMode('folderless');
                  setSelectedFolder(null);
                  setLists([]);
                  setSelectedList(null);
                  setStatuses([]);
                  setMembers([]);
                }}
              />
              Kunde ist in dem gewählten ClickUp-Space (Ohne Ordner)
            </label>
          </div>
        )}

        {/* Ordner-Auswahl */}
        {folderMode === 'folder' && (
          <Select
            className="select-box"
            value={selectedFolder ? { 
              value: selectedFolder, 
              label: folders.find(fo => fo.id === selectedFolder)?.name 
            } : null}
            onChange={(option) => {
              const val = option?.value || null;
              setSelectedFolder(val);
              setLists([]);
              setSelectedList(null);
              setStatuses([]);
              setMembers([]);
            }}
            options={folders.map((fo) => ({
              value: fo.id,
              label: fo.name
            }))}
            placeholder="Ordner wählen..."
            isClearable
            isSearchable
            styles={customSelectStyles}
          />
        )}

        {/* Listen-Auswahl */}
        {(folderMode === 'folder' || folderMode === 'folderless') && (
          <Select
            className="select-box"
            disabled={!lists.length}
            value={selectedList ? { value: selectedList, label: lists.find((li) => li.id === selectedList)?.name } : null}
            onChange={(option) => {
              setSelectedList(option?.value || null);
              setStatuses([]);
              setMembers([]);
              setTasks([]);
            }}
            options={lists.map((li) => ({
              value: li.id,
              label: li.name
            }))}
            placeholder="Liste wählen..."
            isClearable
            isSearchable
            styles={customSelectStyles}
          />
        )}

        {/* Status-Liste */}
        {selectedList && statuses.length > 0 && (
          <div>
            <strong>Status in der gewählten Liste:</strong>
            <ul className="chips-list">
              {statuses.map((st) => (
                <li key={st.id} className="chip-item">
                  <div
                    className="status-dot"
                    style={{ backgroundColor: st.color }}
                  />
                  {st.status}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Mitglieder-Liste */}
        {selectedList && members.length > 0 && (
          <div>
            <strong>Mitglieder in dieser Liste:</strong>
            <ul className="chips-list">
              {members.map((m) => (
                <li key={m.id} className="chip-item">
                  <img
                    src={m.profilePicture || ''}
                    alt={m.username || 'User'}
                  />
                  {m.username} ({m.email})
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/*==========================================================
  Hauptkomponente: StartWorkflow
==========================================================*/
export default function StartWorkflow() {
  const [workflowTemplates, setWorkflowTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Ausgewählte Vorlage
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  // WorkflowBuilder anzeigen
  const [showBuilder, setShowBuilder] = useState(false);

  // ClickUp-Block aktivieren?
  const [showClickUpBlock, setShowClickUpBlock] = useState(false);

  // Gespeicherter Flow vom Builder
  const [savedStaticFlowJson, setSavedStaticFlowJson] = useState(null);

  // ClickUp-Daten
  const [clickupData, setClickupData] = useState({
    listId: null,
    statuses: [],
    members: [],
    tasks: [],
  });

  // Debug: Überblick in Parent
  useEffect(() => {
    console.log('[StartWorkflow] savedStaticFlowJson?', Boolean(savedStaticFlowJson));
  }, [savedStaticFlowJson]);
  useEffect(() => {
    console.log('[StartWorkflow] clickupData update', {
      listId: clickupData.listId,
      statuses: clickupData.statuses?.length || 0,
      members: clickupData.members?.length || 0,
      tasks: clickupData.tasks?.length || 0
    });
  }, [clickupData]);

  // Step-Index
  const [currentStep, setCurrentStep] = useState(1);

  const userCookie = JSON.parse(localStorage.getItem('user'))

  /*---------------------------------------
    Vorlagen laden + check ob ClickUp
  ----------------------------------------*/
  useEffect(() => {
    const fetchFlowsx = async () => {
      try {
        await fetchFlows(setWorkflowTemplates);
      } catch (error) {
        console.error('Fehler beim Laden der Vorlagen:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchFlowsx();

    if (userCookie.workspace_tool_name?.includes('clickup')) {
      setShowClickUpBlock(true);
    }
  }, []);

  /*---------------------------------------
    Callback: WorkflowBuilder -> Speichern
  ----------------------------------------*/
  const savingFuncx = useCallback((templteRecID, modus, isTemplateEditing, staticJSON) => {
    console.log('Original staticJSON:', staticJSON);
    console.log('Workflow aus Builder gespeichert: ', staticJSON);

    // Beispiel: Rollen extrahieren
    const allRoles = new Set();
    staticJSON.blocks?.forEach((block) => {
      if (block.rolle) {
        allRoles.add(block.rolle);
      }
      block.children_blocks?.forEach((child) => {
        if (child.rolle) allRoles.add(child.rolle);
      });
    });
    staticJSON.roles = Array.from(allRoles);

    // Flow-Daten speichern
    setSavedStaticFlowJson(staticJSON);
    setShowBuilder(false);

    // Step weiter
    if (showClickUpBlock) setCurrentStep(2);
    else setCurrentStep(3);

    return true;
  }, [showClickUpBlock]);

  /*---------------------------------------
    Handler: Vorlage auswählen
  ----------------------------------------*/
  const handleSelectTemplate = useCallback((event) => {
    const templateId = event.target.value;
    const foundTemplate = workflowTemplates.find((t) => t.id === templateId);

    if (!templateId) {
      // Keine Auswahl
      setSelectedTemplate(null);
      setShowBuilder(false);
      setSavedStaticFlowJson(null); // Step3 reset
      return;
    }
    setSelectedTemplate(foundTemplate || null);
    setShowBuilder(true);
    setSavedStaticFlowJson(null); // Step3 reset
  }, [workflowTemplates]);

  /*---------------------------------------
    Handler: Child schickt ClickUp-Daten
    => Endlosschleife-Fix
  ----------------------------------------*/
  const handleSelectClickUpData = useCallback((data) => {
    // Nur aktualisieren, wenn sich wirklich was geändert hat:
    setClickupData((prev) => {
      // 1) check listId
      if (prev.listId !== data.listId) {
        return data; // Die Liste hat sich geändert => update
      }
      // 2) check statuses
      if (!isSameArray(prev.statuses, data.statuses)) {
        return data; // update
      }
      // 3) check members
      if (!isSameArray(prev.members, data.members)) {
        return data; // update
      }
      // 4) check tasks
      if (!isSameArray(prev.tasks, data.tasks)) {
        return data; // update
      }
      // Ansonsten: keine Änderung => Original zurückgeben
      return prev;
    });
  }, []);

  /*---------------------------------------
    Handler: Finaler Start
  ----------------------------------------*/
  const handleStartWorkflow = useCallback((payload) => {
    const flattened = flattenFlow(payload);
    
    // Konvertiere das Datum von YYYY-MM-DD zu DD.MM.YYYY
    const convertDate = (dateStr) => {
        const [year, month, day] = dateStr.split('-');
        return `${day}.${month}.${year}`;
    };
    
    const flattendWithDates = calculateDueDates(flattened, convertDate(payload.startDate))
    console.log("---- FLATTENED DATES ----");
    console.log(JSON.stringify(flattendWithDates, null, 2));    

    const AirTableWorkflowData = {
      start_datum: payload.startDate,
      todoTask_status: payload.unlockedStatus,
      workflow_status: payload.projektStatus,
      name: payload.workflowName,
      clickUpListID: payload.clickUpListID,
      integromat_generation_status: "queue",
      namingConvention: JSON.stringify(payload.namingConvention, null, 2),
      calculated_flow: JSON.stringify(flattendWithDates, null, 2),
      automatisierung: payload.automatisierung ? payload.automatisierung : '',  // Automatisierung hinzufügen
      bestands_workdlow_id: payload.workflowMode === 'extend' && payload.bestands_workdlow_id ? payload.bestands_workdlow_id : ''
    }

    createRecordInAirTable(AirTableWorkflowData,"flows")
    alert("Workflow Erstellt")
    window.location.reload();
  }, []);

  /*---------------------------------------
    Render
  ----------------------------------------*/
  if (isLoading) {
    return <div style={{ color: '#fff', textAlign: 'center' }}>Lade Vorlagen...</div>;
  }

  return (
    <div className="workflow-wizard">
      <h2>Workflow starten</h2>

      {/* STEP 1 */}
      <div className="step">
        <div className="step-header">
          <div className="step-number">1</div>
          <h4 className="step-title">Vorlage auswählen</h4>
        </div>
        <div className="step-content">
          <Select
            className="select-box"
            value={selectedTemplate ? { value: selectedTemplate.id, label: selectedTemplate.fields?.name || 'Unbenannte Vorlage' } : null}
            onChange={(option) => {
              if (!option) {
                setSelectedTemplate(null);
                setShowBuilder(false);
                setSavedStaticFlowJson(null);
                return;
              }
              const foundTemplate = workflowTemplates.find((t) => t.id === option.value);
              setSelectedTemplate(foundTemplate || null);
              setShowBuilder(true);
              setSavedStaticFlowJson(null);
            }}
            options={workflowTemplates.map(tpl => ({
              value: tpl.id,
              label: tpl.fields?.name || 'Unbenannte Vorlage'
            }))}
            placeholder="-- Vorlage auswählen --"
            isClearable
            isSearchable
            styles={customSelectStyles}
          />

          {showBuilder && selectedTemplate && (
            <div style={{ marginTop: '1rem' }}>
              <WorkflowBuilder
                isTemplateEditing={false}
                modus="editFlow"
                templteRecID={selectedTemplate.id}
                savingFunc={savingFuncx}
                closeChartFun={() => setShowBuilder(false)}
              />
            </div>
          )}
        </div>
      </div>

      {/* STEP 2: ClickUp-Block */}
      {showClickUpBlock && savedStaticFlowJson && (
        <ClickUpBlock
          userCookie={userCookie}
          onSelectClickUpData={handleSelectClickUpData}
        />
      )}

      {/* STEP 3: Match-Box */}
      {savedStaticFlowJson && clickupData.listId && (
        <MatchBox
          clickupListMembers={clickupData.members}
          clickUpListStatis={clickupData.statuses}
          clickUpListID={clickupData.listId}
          clickupListTasks={clickupData.tasks}
          savedStaticFlowJson={savedStaticFlowJson}
          onStartWorkflow={handleStartWorkflow}
        />
      )}
    </div>
  );
}

// Globales Styles-Objekt für react-select
const customSelectStyles = {
  control: (baseStyles, state) => ({
    ...baseStyles,
    backgroundColor: '#3c3c3c',
    borderColor: state.isFocused ? 'var(--accent-color)' : 'var(--border-color)',
    boxShadow: state.isFocused ? '0 0 0 1px var(--accent-color)' : 'none',
    '&:hover': {
      borderColor: 'var(--accent-color)'
    }
  }),
  menu: (baseStyles) => ({
    ...baseStyles,
    backgroundColor: '#3c3c3c',
    border: '1px solid var(--border-color)',
    borderRadius: '8px'
  }),
  option: (baseStyles, state) => ({
    ...baseStyles,
    backgroundColor: state.isSelected 
      ? 'var(--accent-color)' 
      : state.isFocused 
        ? '#4a4a4a' 
        : '#3c3c3c',
    color: state.isSelected ? '#fff' : 'var(--text-color)',
    '&:hover': {
      backgroundColor: '#4a4a4a'
    }
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    color: 'var(--text-color)'
  }),
  input: (baseStyles) => ({
    ...baseStyles,
    color: 'var(--text-color)'
  }),
  placeholder: (baseStyles) => ({
    ...baseStyles,
    color: '#888'
  }),
  dropdownIndicator: (baseStyles) => ({
    ...baseStyles,
    color: 'var(--text-color)'
  }),
  clearIndicator: (baseStyles) => ({
    ...baseStyles,
    color: 'var(--text-color)'
  }),
  indicatorSeparator: (baseStyles) => ({
    ...baseStyles,
    backgroundColor: 'var(--border-color)'
  })
};
